'use client'

import { Fragment, useEffect, useRef, useState, useCallback } from 'react'
import { RatingCard } from '@/components/rating-card'
import { AdRectangle } from '@/components/adsense'

// Insert an ad after every Nth rating card in the infinite feed.
const AD_FREQUENCY = 11

interface FeedRating {
  id: string
  overallRating: number
  comment: string | null
  ratedAt: string
  user: { name: string | null; image: string | null }
  entity: {
    id: string
    slug: string
    name: string
    imageUrl: string | null
    externalId: string
    ratingSummary: { currentRating: number; totalRatings: number } | null
    parent: {
      name: string
      externalId: string
      parent?: {
        externalId: string
        parent?: { externalId: string } | null
      } | null
    } | null
  }
}

interface RatingsFeedProps {
  initialRatings: FeedRating[]
  initialCursor: string | null
}

export function RatingsFeed({ initialRatings, initialCursor }: RatingsFeedProps) {
  const [ratings, setRatings] = useState<FeedRating[]>(initialRatings)
  const [cursor, setCursor] = useState<string | null>(initialCursor)
  const [isLoading, setIsLoading] = useState(false)
  const [hasMore, setHasMore] = useState(initialCursor !== null)
  const sentinelRef = useRef<HTMLDivElement | null>(null)

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore || !cursor) return
    setIsLoading(true)
    try {
      const res = await fetch(`/api/ratings/recent?cursor=${encodeURIComponent(cursor)}`)
      if (!res.ok) throw new Error('Failed to load')
      const data = await res.json()
      setRatings((prev) => [...prev, ...data.ratings])
      setCursor(data.nextCursor)
      setHasMore(data.nextCursor !== null)
    } catch (err) {
      console.error('Error loading more ratings:', err)
    } finally {
      setIsLoading(false)
    }
  }, [isLoading, hasMore, cursor])

  useEffect(() => {
    const node = sentinelRef.current
    if (!node) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { rootMargin: '400px' }
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [loadMore])

  if (ratings.length === 0) {
    return (
      <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
        <p className="text-slate-600">No ratings with comments yet. Be the first!</p>
      </div>
    )
  }

  return (
    <div>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
        {ratings.map((r, i) => (
          <Fragment key={r.id}>
            <div className="mb-6 break-inside-avoid">
              <RatingCard
                entity={r.entity}
                rating={r.entity.ratingSummary?.currentRating ?? r.overallRating}
                totalRatings={r.entity.ratingSummary?.totalRatings}
                comment={{
                  userName: r.user.name,
                  userImage: r.user.image,
                  text: r.comment,
                  ratedAt: r.ratedAt,
                  overallRating: r.overallRating,
                }}
              />
            </div>
            {(i + 1) % AD_FREQUENCY === 0 && i < ratings.length - 1 && (
              <div className="mb-6 break-inside-avoid">
                <AdRectangle className="my-0" index={Math.floor(i / AD_FREQUENCY)} />
              </div>
            )}
          </Fragment>
        ))}
      </div>

      <div ref={sentinelRef} className="h-12 flex items-center justify-center mt-6">
        {isLoading && (
          <div className="text-slate-500 text-sm">Loading more…</div>
        )}
        {!hasMore && ratings.length > 0 && (
          <div className="text-slate-400 text-sm">You&apos;ve reached the end.</div>
        )}
      </div>
    </div>
  )
}
